import React, { useCallback, useContext, useEffect } from 'react';
import LogRocket from 'logrocket';
import * as Sentry from '@sentry/browser';
import _get from 'lodash/get';

import { AuthContext } from './AuthContext';
import { useAppContext } from 'utils/hooks';
import { preventErrorLog } from 'utils/methods';

const RR_API_KEY = process.env.REACT_APP_RR_LOG_ROCKET_APP_ID;
const SSR_API_KEY = process.env.REACT_APP_SSR_LOG_ROCKET_APP_ID;
const IS_PROD = process.env.REACT_APP_MODE === 'PRODUCTION';

const DISCARD_URL_PATTERNS = [/\/auth/, /\/seller\/oauth/];
const REDACT_HEADERS = ['Seller-Store-Guid', 'Rr-Store-Id'];

/**
 * @type {import('react').Context<{
 *   track: (eventName: string, eventProperties?: Record<string, any>) => void;
 * }>}
 */
export const LogRocketContext = React.createContext();

export function LogRocketProvider({ children = null }) {
  const { userData, storeProperties } = useContext(AuthContext);
  const { isSSRMode } = useAppContext();

  const storeName = _get(storeProperties, 'store_name', '');
  const ssSellerId = _get(storeProperties, 'ss_seller_id', '');
  const storeUniqueName = _get(storeProperties, 'store_unique_name', '');
  const isSSR = _get(storeProperties, 'is_ssr_retailer', false);
  const apiKey = isSSRMode ? SSR_API_KEY : RR_API_KEY;

  const isTrackable = apiKey && IS_PROD && !preventErrorLog(userData);

  useEffect(() => {
    if (isTrackable) {
      try {
        LogRocket.init(apiKey, {
          console: { shouldAggregateConsoleErrors: true },
          release: `${process.env.REACT_APP_VERSION}-${process.env.REACT_APP_CURRENT_GIT_SHA}`,
          network: {
            requestSanitizer: request => {
              request.body = sanitizeBody(request);
              request.headers = sanitizeHeaders(request);
              return request;
            },
            responseSanitizer: response => {
              response.body = sanitizeBody(response);
              return response;
            }
          }
        });
        LogRocket.getSessionURL(sessionURL => {
          Sentry.configureScope(scope => {
            scope.setExtra('sessionURL', sessionURL);
          });
        });
      } catch (error) {
        console.error('Failed to initialize LogRocket:', error);
      }
    }
  }, [apiKey, isTrackable]);

  useEffect(() => {
    const userId = _get(userData, 'userId');
    const email = _get(userData, 'email');
    const firstName = _get(userData, 'firstName');
    const lastName = _get(userData, 'lastName');

    if (isTrackable && userId) {
      try {
        LogRocket.identify(userId, {
          name: `${firstName} ${lastName}`.trim(),
          email,
          isSSR,
          isSSRMode,
          storeName,
          storeUniqueName,
          sellerId: ssSellerId,
          userRole: isSSRMode ? 'SS-ADMIN' : 'RR-ADMIN'
        });
      } catch (error) {
        console.error('Failed to identify user in LogRocket:', error);
      }
    }
  }, [
    isTrackable,
    userData,
    isSSR,
    isSSRMode,
    storeName,
    storeUniqueName,
    ssSellerId
  ]);

  const track = useCallback(
    (eventName = '', eventProperties = {}) => {
      if (!isTrackable || !eventName) return;

      try {
        LogRocket.track(eventName, eventProperties);
      } catch (error) {
        console.error('Failed to track event in LogRocket:', error);
      }
    },
    [isTrackable]
  );

  return (
    <LogRocketContext.Provider value={{ track }}>
      {children}
    </LogRocketContext.Provider>
  );
}

/**
 * @param {Request} request
 */
function sanitizeHeaders(request) {
  const headers = { ...request.headers };
  REDACT_HEADERS.forEach(header => {
    if (header in headers) {
      headers[header] = '*';
    }
  });
  return headers;
}

/**
 * @param {Request | Response} data
 */
function sanitizeBody(data) {
  const discardBody = DISCARD_URL_PATTERNS.some(pattern => {
    return pattern.test(data.url);
  });
  return discardBody ? null : data.body;
}
